import { useState } from 'react';
import { formatServerDateTime, useDisplayZone, useSession } from '@adiox/ui';
import { useMailbox } from './useMailbox';

/** MT5's Mailbox: messages from the broker, newest first; opening one marks it read. */
export function MailboxPanel() {
  const { messages, unread, loaded, failed, retry, markRead, remove } = useMailbox();
  const readOnly = useSession().user?.readOnly === true;
  const zone = useDisplayZone();
  const [openId, setOpenId] = useState<number | null>(null);
  const open = messages.find((m) => m.id === openId) ?? null;

  if (readOnly) {
    return (
      <p className="p-4 text-sm text-muted">The mailbox is not shown in a read-only (investor) session.</p>
    );
  }
  if (failed) {
    return (
      <div role="alert" className="flex items-center gap-3 p-4 text-sm text-down">
        Could not load the mailbox.
        <button
          type="button"
          className="rounded-md border border-line px-3 py-1 text-fg hover:border-accent"
          onClick={retry}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <section className="flex h-full min-h-0 bg-panel text-sm" aria-label="Mailbox">
      <ul
        className="w-2/5 min-w-48 overflow-auto border-r border-line"
        aria-label={`Messages, ${unread} unread`}
      >
        {!loaded && <li className="p-3 text-muted">Loading…</li>}
        {loaded && messages.length === 0 && <li className="p-3 text-muted">No messages.</li>}
        {messages.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              data-testid="mail-row"
              aria-current={m.id === openId}
              onClick={() => {
                setOpenId(m.id);
                if (!m.read) void markRead(m.id);
              }}
              className={`block w-full border-b border-line/40 px-3 py-2 text-left hover:bg-panel2 ${m.id === openId ? 'bg-panel2' : ''}`}
            >
              <span className="flex justify-between gap-2 text-xs text-muted">
                <span>{m.from}</span>
                <span className="num">{formatServerDateTime(m.sentAt, zone.offsetAt).slice(0, 16)}</span>
              </span>
              <span className={`block truncate ${m.read ? '' : 'font-semibold'}`}>
                {!m.read && <span className="sr-only">Unread: </span>}
                {!m.read && (
                  <span aria-hidden="true" className="mr-1 text-accent">
                    ●
                  </span>
                )}
                {m.subject}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <article className="min-w-0 flex-1 overflow-auto p-3" aria-label="Message">
        {open ? (
          <>
            <header className="mb-3 border-b border-line pb-2">
              <h3 className="text-base font-semibold">{open.subject}</h3>
              <p className="text-xs text-muted">
                From {open.from} · {formatServerDateTime(open.sentAt, zone.offsetAt)} ({zone.label})
              </p>
            </header>
            {/* plain text, never HTML: a message can never run script in the terminal */}
            <p className="whitespace-pre-wrap" data-testid="mail-body">
              {open.body}
            </p>
            <button
              type="button"
              className="mt-4 rounded border border-line px-3 py-1 text-xs hover:border-down hover:text-down"
              onClick={() => {
                setOpenId(null);
                void remove(open.id);
              }}
            >
              Delete
            </button>
          </>
        ) : (
          <p className="text-muted">Choose a message to read it.</p>
        )}
      </article>
    </section>
  );
}
